import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Ticker = { price: number; change_24h: number; volume_24h: number; market_cap: number };

async function priceFeedAction(action: string, payload: Record<string, any> = {}) {
  const { data, error } = await supabase.functions.invoke("price-feed", {
    body: { action, ...payload },
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Direct database fallback used when the price-feed edge function is
 * unavailable (e.g. 503 SUPABASE_EDGE_RUNTIME_SERVICE_DEGRADED).
 * Prices come from the same source the function reads first, so the UI
 * degrades gracefully instead of throwing / blanking the screen.
 */
const STABLECOINS = new Set(["USD", "USDT", "USDC", "BUSD", "DAI", "TUSD", "USDP", "FDUSD", "PYUSD", "USDD"]);

async function dbTickers(assets: string[]): Promise<Record<string, Ticker>> {
  const symbols = [...new Set(assets.map((a) => a.toUpperCase()))];
  if (symbols.length === 0) return {};
  const out: Record<string, Ticker> = {};
  for (const s of symbols) {
    if (STABLECOINS.has(s)) out[s] = { price: 1, change_24h: 0, volume_24h: 0, market_cap: 0 };
  }

  const { data } = await supabase
    .from("token_configs")
    .select("symbol, price_usd")
    .eq("status", "active")
    .in("symbol", symbols);

  for (const row of data || []) {
    const price = Number((row as any).price_usd) || 0;
    if (price > 0) out[(row as any).symbol] = { price, change_24h: 0, volume_24h: 0, market_cap: 0 };
  }
  return out;
}

export function usePriceTicker(asset?: string) {
  return useQuery({
    queryKey: ["price-ticker", asset],
    queryFn: async () => {
      try {
        return await priceFeedAction("get_ticker", { asset });
      } catch {
        const fallback = await dbTickers([asset!]);
        return fallback[asset!.toUpperCase()] ?? { price: 0, change_24h: 0, volume_24h: 0, market_cap: 0 };
      }
    },
    enabled: !!asset,
    refetchInterval: 10_000,
    staleTime: 5_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });
}

export function usePriceCandles(asset?: string, interval = "1h", limit = 200) {
  return useQuery({
    queryKey: ["price-candles", asset, interval, limit],
    queryFn: async () => {
      try {
        const result = await priceFeedAction("get_candles", { asset, interval, limit });
        return result.candles || [];
      } catch {
        return [];
      }
    },
    enabled: !!asset,
    staleTime: 30_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });
}

export function useMultiPriceTicker(assets?: string[]) {
  return useQuery({
    queryKey: ["price-multi-ticker", assets?.slice().sort().join(",")],
    queryFn: async () => {
      try {
        const result = await priceFeedAction("get_multi_ticker", { assets });
        return (result.tickers || {}) as Record<string, Ticker>;
      } catch {
        return await dbTickers(assets || []);
      }
    },
    enabled: !!assets && assets.length > 0,
    refetchInterval: 15_000,
    staleTime: 10_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });
}
